import React, { PropsWithChildren } from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

interface Props {
  children: React.ReactNode;
  className?: string;
}

type I18nMainKey =
  | 'userSettings:account.title'
  | 'userSettings:notationSystems.title'
  | 'userSettings:password.title'
  | 'userSettings:contact.title'
  | 'userSettings:restore.title'
  | 'userSettings:aiUsage.title'
  | 'userSettings:customFields.title'
  | 'userSettings:schoolSubscription.licensesToShare'
  | 'userSettings:account.direction.title'
  | 'userSettings:schoolSubscription.direction.title'
  | 'schoolyearSettings:setUpHolidaysAndPeriods'
  | 'schoolyearSettings:deleteSchoolyear';

type I18nTitleKey =
  | 'userSettings:referentiels.title'
  | 'userSettings:curriculumLabels.title'
  | 'userSettings:materials.title';

type I18nSectionKey =
  | 'schoolyearSettings:schoolContacts.title'
  | 'schoolyearSettings:schoolContacts.subtitle'
  | 'schoolyearSettings:schoolPasswords.title'
  | 'schoolyearSettings:schoolPasswords.subtitle';

interface SectionProps {
  title?: React.ReactNode;
  titleKey?: I18nSectionKey;
  subtitle?: string;
  subtitleKey?: I18nSectionKey;
  blank?: boolean;
  className?: string;
  padding?: boolean;
}

interface MainProps extends PropsWithChildren<SectionProps> {
  i18nKey?: I18nMainKey;
}

const twoColumnsCardSidebarWidthStyle = tw`max-w-7xl px-4 sm:px-6 md:px-8`;
export default function TwoColumnsCardSidebar(props: Props) {
  const { children, className } = props;
  return (
    <main tw="flex-1 relative focus:outline-none" className={className}>
      <div css={twoColumnsCardSidebarWidthStyle}>{children}</div>
    </main>
  );
}

export function SideAndBodyWrapper(props: Props) {
  const { children } = props;
  return <div tw="lg:grid lg:grid-cols-12 lg:gap-x-8">{children}</div>;
}

function Side({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className} tw="pb-2 lg:pb-6 lg:col-span-3">
      <nav
        css={[
          tw`space-y-1 sticky z-10`,
          css`
            top: 88px;
          `,
        ]}
      >
        {children}
      </nav>
    </div>
  );
}

function Title({
  title,
  icon,
  className,
  i18nKey, // i18nKey is prioritized over title if provided
}: {
  title?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  i18nKey?: I18nTitleKey;
}) {
  const { t } = useTranslation();
  return (
    <h2 css={[tw`mb-3`, icon && tw`flex items-center`]} className={className}>
      {icon && <span tw="mr-2">{icon}</span>}
      {i18nKey ? t(i18nKey) : title}
    </h2>
  );
}

function SubTitle({ subtitle }: { subtitle: React.ReactNode }) {
  return <div tw="mt-1! text-neutral-moderate">{subtitle}</div>;
}

export function Section({
  children,
  title,
  titleKey,
  subtitle,
  subtitleKey,
  blank = false,
  className,
  padding = true,
}: PropsWithChildren<SectionProps>) {
  const { t } = useTranslation();
  const titleText = titleKey ? t(titleKey) : title;
  const subtitleText = subtitleKey ? t(subtitleKey) : subtitle;

  return (
    <div className={className} css={[!blank && tw`rounded-lg bg-white`]}>
      <div
        css={[tw`space-y-6`, !blank && tw`py-6`, padding && tw`px-4 sm:px-6`]}
      >
        {titleText && (
          <Title css={[subtitle ? tw`mb-0` : tw`mb-3`]} title={titleText} />
        )}
        {subtitleText && <SubTitle subtitle={subtitleText} />}
        {children}
      </div>
    </div>
  );
}

function Container({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={className} tw="space-y-6 sm:px-6 lg:px-0 lg:col-span-9">
      {children}
    </div>
  );
}

function Header({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={className}
      tw="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap mb-10"
    >
      {children}
    </div>
  );
}

function HeaderLeft({ children }: PropsWithChildren) {
  return <div tw="ml-4 mt-2 mb-2">{children}</div>;
}

function HeaderRight({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={className} tw="ml-auto">
      {children}
    </div>
  );
}

function Main({ children, i18nKey, ...props }: MainProps) {
  const { t } = useTranslation();

  return (
    <Container tw="relative">
      <Section title={t(i18nKey)} {...props}>
        {children}
      </Section>
    </Container>
  );
}

TwoColumnsCardSidebar.Side = Side;
TwoColumnsCardSidebar.Main = Main;
TwoColumnsCardSidebar.SideAndBodyWrapper = SideAndBodyWrapper;
TwoColumnsCardSidebar.Title = Title;
TwoColumnsCardSidebar.SubTitle = SubTitle;
TwoColumnsCardSidebar.Container = Container;
TwoColumnsCardSidebar.Section = Section;
TwoColumnsCardSidebar.Header = Header;
TwoColumnsCardSidebar.HeaderRight = HeaderRight;
TwoColumnsCardSidebar.HeaderLeft = HeaderLeft;
